/**
 * Shared localStorage utilities for chatbot hooks.
 *
 * DRY: Eliminates duplicate storage helpers across hooks (useTraining, useChatbot, useSop).
 *
 * @module features/chatbot/utils/persisted-state.utils
 */

/**
 * Load a value from localStorage with type safety and optional validation.
 *
 * @param key - localStorage key
 * @param defaultValue - Value to return if key doesn't exist or parsing fails
 * @param validator - Optional type guard to validate the parsed value
 * @returns The stored value (if valid) or the default value
 *
 * @example
 * // Simple usage
 * const isOpen = loadFromStorage('chatbot-open', false);
 *
 * @example
 * // With validation
 * const state = loadFromStorage('training-state', defaultState, isValidTrainingState);
 */
export function loadFromStorage<T>(
  key: string,
  defaultValue: T,
  validator?: (value: unknown) => value is T
): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);

      // If validator provided, use it; otherwise trust the parsed value
      if (validator) {
        if (validator(parsed)) {
          return parsed;
        }
        // Invalid data - clear it from storage
        localStorage.removeItem(key);
      } else {
        return parsed as T;
      }
    }
  } catch {
    // Ignore JSON parse errors - return default
  }

  return defaultValue;
}

/**
 * Save a value to localStorage.
 *
 * @param key - localStorage key
 * @param value - Value to store (will be JSON stringified)
 *
 * @example
 * saveToStorage('chatbot-open', true);
 */
export function saveToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (quota exceeded, private mode, etc.)
  }
}

/**
 * Remove a value from localStorage.
 *
 * @param key - localStorage key to remove
 *
 * @example
 * clearFromStorage('training-session');
 */
export function clearFromStorage(key: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore removal errors
  }
}

/**
 * Storage keys used by chatbot features.
 * Centralized for consistency and easier maintenance.
 */
export const CHATBOT_STORAGE_KEYS = {
  /** Main chatbot open/close state */
  UI_STATE: 'axon-chatbot-ui-state',
  /** Training session data */
  TRAINING_SESSION: 'axon-chatbot-training-session',
  /** Training mode active state */
  TRAINING_MODE: 'axon-chatbot-show-training',
  /** SOP mode active state */
  SOP_MODE: 'axon-chatbot-show-sop',
  /** Minimized state */
  MINIMIZED: 'axon-chatbot-minimized',
} as const;

export type ChatbotStorageKey = (typeof CHATBOT_STORAGE_KEYS)[keyof typeof CHATBOT_STORAGE_KEYS];
